import React, { FC, memo, useState, useEffect } from 'react'
import cn from 'classnames'

import ProductForm from './ProductForm'
import DivisionsForm from './DivisionsForm'
import InventoryEvents from './InventoryEvents'
import LocationsForm from './LocationsForm'

import { IProduct, IProductTag } from '../../types/products'
import { IAccount, IBranch, IDivision } from '../../types/user'
import { IHash } from '../../types/common'
import DateUpdated from '../../components/DateUpdated'

interface IProductFormProps {
  product: IProduct
  branch: IBranch
  account?: IAccount
  divisions: IDivision[]
  distributors: string[]
  productTags: IProductTag[]
  loading: boolean
  errors: IHash<string>
  truckInventoryFeature: boolean
  binInventoryFeature: boolean
  onTagCreate: (
    name: string,
    color: string,
  ) => Promise<{ errors?: any; result?: IProductTag }>
  onSave: (product: IProduct) => void
  onRemove: (product: IProduct) => void
  onCancel: () => void
  onRefetch: () => void
  onSyncProduct: () => void
  onProductInventoryUpdate: (
    patch: Partial<Pick<IProduct, 'inventory' | 'updatedAt'>>,
  ) => void
}

type ProductInfoTab = 'info' | 'history' | 'divisions' | 'locations'

const ProductInfo: FC<IProductFormProps> = ({
  product,
  branch,
  onSave,
  loading,
  account,
  truckInventoryFeature,
  binInventoryFeature,
  onSyncProduct,
  onProductInventoryUpdate,
  onRefetch,
  ...rest
}) => {
  const [tab, setTab] = useState<ProductInfoTab>('info')
  const [saving, setSaving] = useState(false)

  const isNew = !product.id

  const filters: Array<{ name: string; type: ProductInfoTab }> = [
    { name: 'Info', type: 'info' },
    { name: 'History', type: 'history' },
    { name: 'Divisions', type: 'divisions' },
  ]

  if (!isNew) {
    filters.push({ name: 'Locations', type: 'locations' })
  }

  useEffect(() => {
    setTab('info')
  }, [product.id])

  const onFinish = async (product: IProduct) => {
    setSaving(true)
    await onSave(product)
    setSaving(false)
  }

  return (
    <div className="product-info">
      <div className="product-info_section">
        <div className="product-info_title">
          {isNew ? (
            <>New Product</>
          ) : (
            <>
              {filters.map((f) => (
                <div
                  key={f.type}
                  className={cn('product-info_toggler', {
                    '-active': f.type === tab,
                  })}
                  onClick={() => {
                    setTab(f.type)
                    if (f.type === 'locations') onRefetch()
                    else if (f.type !== 'history') onSyncProduct()
                  }}
                >
                  {f.name}
                </div>
              ))}
              <DateUpdated
                loading={loading}
                updatedAt={product.updatedAt}
                saving={saving}
              />
            </>
          )}
        </div>

        {tab === 'info' && (
          <ProductForm
            key={product.id}
            product={product}
            branch={branch}
            onSave={onFinish}
            loading={loading}
            account={account}
            binInventoryFeature={binInventoryFeature}
            onRefetch={onRefetch}
            {...rest}
          />
        )}
        {tab === 'divisions' && (
          <DivisionsForm
            product={product}
            onSave={onFinish}
            loading={loading}
            {...rest}
          />
        )}
        {tab === 'history' && (
          <InventoryEvents
            product={product}
            branch={branch}
            account={account}
            onProductRefresh={onProductInventoryUpdate}
            {...rest}
          />
        )}
        {tab === 'locations' && (
          <LocationsForm
            product={product}
            loading={loading}
            onSave={onFinish}
            truckInventoryFeature={truckInventoryFeature}
            binInventoryFeature={binInventoryFeature}
            {...rest}
          />
        )}
      </div>
    </div>
  )
}

export default memo(ProductInfo)
